import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import { useUser } from "../../context/userContext"; // Assuming you have a user context
import { Box, Card, CardContent, Typography, Grid, CircularProgress, Divider,Button } from '@mui/material';

const PurchaseHistory = () => {
  const [orders, setOrders] = useState([]); // Store the orders fetched from the API
  const [searchQuery, setSearchQuery] = useState('');
  const [loggedInUserEmail, setLoggedInUserEmail] = useState(''); // Initially empty
  const [filteredOrders, setFilteredOrders] = useState([]); // Store the filtered orders

  const { user } = useUser(); // Get the logged-in user data from context

  // Set the logged-in user's email when user data is available
  useEffect(() => {
    if (user && user.email) {
      setLoggedInUserEmail(user.email);
    }
  }, [user]);

  // Function to fetch orders from the backend
  const fetchOrders = async () => {
    try {
      const response = await fetch('https://api.theonlysale.com/api/order/get'); // Make the API request to the backend
      const data = await response.json(); // Parse the JSON response
      setOrders(data); // Set the orders from the response
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  // Fetch orders when the component mounts
  useEffect(() => {
    fetchOrders();
  }, []);

// Filter orders based on the logged-in user's email and search query
useEffect(() => {
  if (loggedInUserEmail) {
    const filtered = orders.filter((order) =>
      order.email === loggedInUserEmail && // Match the order's email with logged-in user's email
      (
        (order.order_id && order.order_id.toString().includes(searchQuery)) || // Ensure order_id exists and is converted to a string
        (order.name && order.name.toLowerCase().includes(searchQuery.toLowerCase())) // Ensure name exists and convert it to lowercase for search
      )
    );
    setFilteredOrders(filtered);
  }
}, [orders, searchQuery, loggedInUserEmail]);

const accountStatus = user?.account_status;

if (accountStatus !== 'active') {
    return (
      <Box className="p-3 min-h-screen dark:bg-gray-900 bg-gray-100" sx={{ marginLeft: { xs: 0, md: '240px' } }}>
        <Sidebar />
        <Card className="p-4 rounded-lg shadow-md dark:bg-gray-800 dark:text-white bg-white text-gray-900">
          <CardContent>
            <Typography variant="h6" gutterBottom className="text-lg font-semibold dark:text-gray-100 text-gray-800">
              Subscription Expired
            </Typography>
            <Typography className="mt-2 dark:text-gray-400 text-gray-700">
              Your subscription has expired. To access the dashboard, please renew your subscription from your profile.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2, borderRadius: '8px' }}
              href="/profile"
              className="mt-4 bg-blue-500 hover:bg-blue-600 text-white dark:bg-blue-700 dark:hover:bg-blue-800"
            >
              Go to Profile
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }
  return (
    <>
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <div className="w-64">
          <Sidebar />
        </div>
        <section className="flex-grow bg-gray-50 dark:bg-gray-900 p-3 sm:p-5 antialiased">
          <div className="mx-auto max-w-screen-xl px-4 lg:px-12">
            <div className="bg-white dark:bg-gray-800 relative shadow-md sm:rounded-lg overflow-hidden">
              <div className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 p-4">
                {/* Search Input */}
                <div className="w-full md:w-1/2">
                  <form className="flex items-center">
                    <label htmlFor="simple-search" className="sr-only">
                      Search
                    </label>
                    <div className="relative w-full">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <i className="fa fa-search text-gray-500 dark:text-gray-400"></i>
                      </div>
                      <input
                        type="text"
                        id="simple-search"
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                        placeholder="Search Orders by ID or Name"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)} // Update searchQuery on input change
                      />
                    </div>
                  </form>
                </div>
              </div>

              {/* Orders Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Order ID</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Customer Name</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length > 0 ? (
                      filteredOrders.map((order) => (
                        <tr key={order.order_id} className="border-b dark:border-gray-700">
                          <td className="px-4 py-3">{order.order_id}</td>
                          <td className="px-4 py-3">{order.name}</td>
                          <td className="px-4 py-3">{order.status}</td>
                          <td className="px-4 py-3">${order.totalAmount.toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-6 py-3 text-center text-gray-500 dark:text-gray-400">
                          No orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PurchaseHistory;
